import React from 'react';
import { Card, Button, Row, Col, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ReceivedPendingRequest, SentPendingRequest } from '../../services/userService';

// Define a common structure for user details within the request
interface RequestUser {
    id: number;
    name: string;
    profession?: string;
    company?: string;
}

interface RequestCardProps {
    request: ReceivedPendingRequest | SentPendingRequest;
    type: 'received' | 'sent';
    onAccept?: (requestId: number) => void;
    onDecline?: (requestId: number) => void;
    onCancel?: (requestId: number) => void;
    isProcessing?: boolean; // Flag to show loading state on buttons
    processingId?: number | null; // Which specific card is processing
}

const RequestCard: React.FC<RequestCardProps> = ({ 
    request, 
    type, 
    onAccept, 
    onDecline, 
    onCancel,
    isProcessing = false,
    processingId = null
}) => {
    const { id: requestId, note, createdAt } = request;
    const isCurrentCardProcessing = isProcessing && processingId === requestId;

    let userDetails: RequestUser | null = null;
    if (type === 'received' && 'requester' in request) {
        userDetails = request.requester;
    } else if (type === 'sent' && 'recipient' in request) {
        userDetails = request.recipient;
    }

    const userName = userDetails?.name || 'Unknown User';
    const userProfession = userDetails?.profession || '';
    const userCompany = userDetails?.company || '';
    const userProfileLink = userDetails ? `/profile/${userDetails.id}` : '#';

    // Format the date (optional, can enhance later)
    const requestDate = new Date(createdAt).toLocaleDateString();

    return (
        <Card className="mb-3 border-0 shadow-sm request-card">
            <Card.Body>
                <Row className="align-items-center">
                    <Col xs="auto">
                        {/* Placeholder Profile Picture */}
                        <div 
                            className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center" 
                            style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}
                        >
                            {userName.charAt(0).toUpperCase()}
                        </div>
                    </Col>
                    <Col>
                        <h5 className="mb-1">
                            <Link to={userProfileLink} className="text-decoration-none">
                                {userName}
                            </Link>
                        </h5>
                        {(userProfession || userCompany) && (
                            <p className="mb-1 text-muted small">
                                {userProfession}{userCompany ? ` at ${userCompany}` : ''}
                            </p>
                        )}
                        {note && <p className="mb-1 fst-italic text-muted small">Note: "{note}"</p>}
                        <p className="mb-0 text-muted small">{type === 'received' ? 'Requested' : 'Sent'} on {requestDate}</p>
                    </Col>
                    <Col xs="auto" className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-2">
                        {type === 'received' && onAccept && onDecline && (
                            <>
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={() => onDecline(requestId)}
                                    disabled={isCurrentCardProcessing}
                                >
                                    {isCurrentCardProcessing ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <i className="fas fa-times me-1"></i>}
                                    Decline
                                </Button>
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    onClick={() => onAccept(requestId)}
                                    disabled={isCurrentCardProcessing}
                                >
                                    {isCurrentCardProcessing ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <i className="fas fa-check me-1"></i>}
                                    Accept
                                </Button>
                            </>
                        )}
                        {type === 'sent' && onCancel && (
                            <Button 
                                variant="outline-warning" 
                                size="sm" 
                                onClick={() => onCancel(requestId)}
                                disabled={isCurrentCardProcessing}
                            >
                                {isCurrentCardProcessing ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <i className="fas fa-ban me-1"></i>}
                                Cancel Request
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default RequestCard; 